import { Icon, IncrementField } from '@design-system';
import { useConvertCurrency } from '@utils/currencyConverter';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { FC } from 'react';

import { AML_LIMIT } from '../../../constants/product';
import { useCurrency } from '../../../hooks/useCurrency';
import { Frame, FrameOptionConsolidated } from '../../../types/Products';
import { Image } from '../../Image';
import { ProductPrice } from '../../ProductPrice/ProductPrice';

interface BasketReviewItemProps {
  disabled: boolean;
  maximumSalesCapacity: number | null;
  onQuantity: (quantity: number) => void;
  onRemove?: () => void;
  onFrameClick?: () => void;
  product: Product;
  quantity: number;
  allowMultiples?: boolean;
  frame?: Frame | null;
  frameIncluded: boolean;
  frameOptions?: Array<FrameOptionConsolidated>;
}

export const BasketReviewItem: FC<BasketReviewItemProps> = ({
  allowMultiples = false,
  disabled,
  frame,
  frameIncluded = false,
  frameOptions = [],
  maximumSalesCapacity,
  onFrameClick,
  onQuantity,
  onRemove,
  product,
  quantity,
}) => {
  const { t } = useTranslation('product');
  const [userCurrency] = useCurrency();
  const { convert } = useConvertCurrency();
  const currency = product.currencyCode || userCurrency;
  const price = parseInt(product.price, 10);
  const amlThreshold = convert(AML_LIMIT, currency);

  const max = maximumSalesCapacity || Math.floor(amlThreshold / price);

  const getLabel = () => {
    if (frameIncluded && frameOptions) {
      return frameOptions[0].title;
    }

    if (!frameIncluded && frame) {
      return frame.title;
    }

    if (!frameIncluded && !frame) {
      return t('drawers.review.unframedTitle');
    }

    return undefined;
  };

  return (
    <div className="flex flex-col rounded-sm border border-divider/20 p-4">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12">
          <Image alt={product.title} src={product.image} width={48} />
        </div>
        <div className="flex flex-1 justify-between">
          <div className="flex flex-col overflow-hidden text-ellipsis">
            <span className="font-bold">{product.artist}</span>
            <span>{product.title}</span>
          </div>
          <div>
            <ProductPrice price={price * quantity} />
          </div>
        </div>
      </div>
      <div className="flex items-end justify-between gap-3">
        {onRemove && (
          <button className="hyperlink" onClick={onRemove} type="button">
            {t('remove')}
          </button>
        )}
        {allowMultiples && (
          <div className="ml-auto flex">
            <IncrementField
              disabled={disabled}
              max={max}
              onChange={onQuantity}
              value={quantity}
            />
          </div>
        )}
      </div>

      {(frame || frameOptions.length > 0) && !frameIncluded ? (
        <div className="col-span-12 pt-3 md:pt-4">
          <div className="col-span-12 border-t border-divider/20 pt-3 md:pt-4">
            <div>
              <button
                className={classNames(
                  'group flex w-full items-start justify-between text-left transition-colors duration-300 ease-out',
                  {
                    'hover:text-neutral-4': !disabled,
                    'cursor-not-allowed text-neutral-4': disabled,
                  },
                )}
                disabled={disabled}
                onClick={() => !disabled && onFrameClick && onFrameClick()}
                type="button"
              >
                <div className="flex flex-col">
                  <span>{getLabel()}</span>
                  <span className="text-neutral-4">{frame?.variant.title}</span>
                </div>
                <div className="flex gap-2">
                  {frame &&
                    frame.variant.consolidatedVariants[0].price !== 0 &&
                    !frameIncluded && (
                      <ProductPrice
                        prefix="+"
                        price={
                          frame.variant.consolidatedVariants[0].price *
                          frame.variant.consolidatedVariants[0].quantity
                        }
                      />
                    )}

                  <Icon name="ic_chevron_right" />
                </div>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
